import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BarChart3, TrendingUp, MousePointerClick, Mail, FileText, Building2, Users,
  FilePlus, MoreHorizontal, ExternalLink, Download, Trash2, Car, Calendar,
  Handshake, PenLine, ArrowRight, CheckCircle2, DollarSign, Send, Eye,
  PieChart as PieIcon, BarChart2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { usePdfPreview } from "@/components/PdfPreviewDialog";
import { CommissionCard, CommissionUnavailable, useCommissionsSnapshot } from "@/components/CommissionCard";
import OnboardingSnapshot from "@/components/OnboardingSnapshot";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

// AWG chart palette — navy → gold with muted accents so the eye
// naturally reads a hierarchy (biggest agent first).
const CHART_COLORS = [
  "#0B132B", "#D4AF37", "#1C2541", "#B8942F", "#3A506B",
  "#6C757D", "#8A6D25", "#5C6B7D", "#C4A030", "#2A3548",
];

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());
const pct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
const fmtMoney = (n) => (n == null || n === "" ? "—" : `$${Math.round(Number(n)).toLocaleString()}`);
const isoDaysAgo = (d) => {
  const t = new Date(); t.setDate(t.getDate() - d);
  return t.toISOString().slice(0, 10);
};

export default function DashboardReports() {
  const { user } = useAuth();
  const role = user?.role || "agent";
  const canDrillAgency = role === "admin";
  const canDrillAgent = role === "admin" || role === "agency_admin";

  const [start, setStart] = useState(isoDaysAgo(30));
  const [end, setEnd] = useState(isoDaysAgo(0));
  const [agencyFilter, setAgencyFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");

  const [analytics, setAnalytics] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("start", new Date(start + "T00:00:00Z").toISOString());
      params.set("end", new Date(end + "T23:59:59Z").toISOString());
      if (agencyFilter) params.set("agency", agencyFilter);
      if (agentFilter) params.set("agent_id", agentFilter);
      const [aRes, pRes, fRes, dRes] = await Promise.all([
        api.get(`/analytics/overview?${params.toString()}`),
        api.get("/proposals").catch(() => ({ data: [] })),
        api.get("/proformas").catch(() => ({ data: [] })),
        api.get("/dealers").catch(() => ({ data: [] })),
      ]);
      setAnalytics(aRes.data);
      setProposals(pRes?.data || []);
      setProformas(fRes?.data || []);
      setDealers(dRes?.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load dashboard");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [start, end, agencyFilter, agentFilter]);

  const chartMax = useMemo(() => {
    if (!analytics?.time_series?.length) return 1;
    return Math.max(1, ...analytics.time_series.map((r) => r.count));
  }, [analytics]);

  // Match proposals to dealers by dealership name (case-insensitive substring)
  const dealerByName = useMemo(() => {
    const m = new Map();
    for (const d of dealers) {
      const key = (d.dealer_name || "").trim().toLowerCase();
      if (key) m.set(key, d);
    }
    return m;
  }, [dealers]);
  const findLinkedDealer = (dealership) => {
    if (!dealership) return null;
    const key = dealership.trim().toLowerCase();
    if (dealerByName.has(key)) return dealerByName.get(key);
    for (const [k, v] of dealerByName.entries()) {
      if (key.includes(k) || k.includes(key)) return v;
    }
    return null;
  };

  const removeProposal = async (id) => {
    if (!window.confirm("Delete this proposal?")) return;
    try {
      await api.delete(`/proposals/${id}`);
      setProposals((p) => p.filter((x) => x.id !== id));
      toast.success("Proposal deleted");
    } catch { toast.error("Delete failed"); }
  };
  const { preview: previewPdf, dialog: pdfDialog } = usePdfPreview();
  const downloadProposalPdf = (id, title) => {
    previewPdf({
      fetcher: () => api.get(`/proposals/${id}/pdf`, { responseType: "blob" }),
      filename: `${(title || "proposal").replace(/[^\w-]+/g, "_")}.pdf`,
      title: `Proposal · ${title || ""}`,
    });
  };

  // ----------------------------------------------------------------------------
  // Snapshot data — hierarchical breakdown per agent (per agency for super-admin)
  // ----------------------------------------------------------------------------
  const groupKey = role === "admin" ? "agency" : "agent";
  const buildBreakdown = (rows, ownerField) => {
    // rows: proposals/proformas/dealers arrays. Each row has user_id.
    // Look up the agent from analytics.agents for name + agency.
    const agentIndex = new Map();
    for (const a of analytics?.agents || []) agentIndex.set(a.agent_id, a);
    const map = new Map();
    for (const row of rows || []) {
      const uid = row[ownerField] || row.user_id;
      const meta = agentIndex.get(uid);
      const label = groupKey === "agency"
        ? (meta?.agency || "Unknown agency")
        : (meta?.agent_name || meta?.agent_email || "Unknown agent");
      map.set(label, (map.get(label) || 0) + 1);
    }
    return [...map.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  };
  // Commissions snapshot — one fetch on mount. Falls back to stubs on error.
  const commissions = useCommissionsSnapshot();
  const groupLabel = role === "admin" ? "Top 5 agencies" : "Top 5 agents";

  const proposalsBreakdown = useMemo(
    () => buildBreakdown(proposals, "user_id"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [proposals, analytics, role]
  );
  const proformasBreakdown = useMemo(
    () => buildBreakdown(proformas, "user_id"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [proformas, analytics, role]
  );
  const dealersBreakdown = useMemo(
    () => buildBreakdown(dealers, "user_id"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealers, analytics, role]
  );

  return (
    <div data-testid="dashboard-reports">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              {role === "admin" ? "Super admin" : role === "agency_admin" ? "Agency" : "Agent"} · Reporting
            </div>
            <p className="text-sm text-[#6C757D] mt-2 max-w-2xl">
              Proposals, proformas, and dealer signups — with a live closing ratio tying every quote back to a signed agreement.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2" data-testid="dashboard-filters">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0B132B]">From</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                className="h-10 rounded-sm border-[#0B132B]/15 font-mono w-40" data-testid="analytics-start" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0B132B]">To</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                className="h-10 rounded-sm border-[#0B132B]/15 font-mono w-40" data-testid="analytics-end" />
            </div>
            <div className="flex gap-1">
              {[7, 30, 90].map((d) => (
                <button key={d} type="button" onClick={() => { setStart(isoDaysAgo(d)); setEnd(isoDaysAgo(0)); }}
                  className="h-10 px-3 text-[10px] uppercase tracking-[0.16em] font-bold border border-[#0B132B]/20 rounded-sm hover:bg-[#0B132B] hover:text-white transition-colors"
                  data-testid={`analytics-preset-${d}`}>{d}d</button>
              ))}
            </div>
          </div>
        </div>

        <OnboardingSnapshot />

        {/* Drill-down */}
        {(canDrillAgency || canDrillAgent) && analytics && (
          <Card className="p-3 mb-6 border-[#E9ECEF] rounded-sm flex flex-wrap gap-4 items-end" data-testid="analytics-drill">
            {canDrillAgency && (
              <div className="min-w-[200px]">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0B132B]">Agency</Label>
                <Select value={agencyFilter || "__all__"} onValueChange={(v) => { setAgencyFilter(v === "__all__" ? "" : v); setAgentFilter(""); }}>
                  <SelectTrigger className="h-9 rounded-sm" data-testid="analytics-agency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All agencies</SelectItem>
                    {(analytics.agencies || []).map((a) => (
                      <SelectItem key={a.agency} value={a.agency}>{a.agency} ({a.proposals})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {canDrillAgent && (
              <div className="min-w-[240px]">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0B132B]">Agent</Label>
                <Select value={agentFilter || "__all__"} onValueChange={(v) => setAgentFilter(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="h-9 rounded-sm" data-testid="analytics-agent-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All agents</SelectItem>
                    {(analytics.agents || []).filter((a) => !agencyFilter || a.agency === agencyFilter).map((a) => (
                      <SelectItem key={a.agent_id} value={a.agent_id}>{a.agent_name} · {a.agency}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(agencyFilter || agentFilter) && (
              <button type="button" onClick={() => { setAgencyFilter(""); setAgentFilter(""); }}
                className="h-9 px-4 text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] hover:text-[#0B132B]" data-testid="analytics-clear-filters">
                Clear filters
              </button>
            )}
          </Card>
        )}

        {/* ============ SNAPSHOT · at-a-glance who's running the business ============ */}
        <Card className="p-4 sm:p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="dashboard-snapshot">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">Snapshot</div>
              <h2 className="font-display text-xl sm:text-2xl font-black text-[#0B132B] leading-none mt-1">
                Overview
              </h2>
            </div>
            <div className="text-[10px] text-[#6C757D] uppercase tracking-[0.16em] hidden sm:block">
              Toggle each card between bar & pie
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SnapshotCard title="Proposals"          icon={FileText}   data={proposalsBreakdown} testid="snapshot-proposals" />
            <SnapshotCard title="Proformas"          icon={BarChart3}  data={proformasBreakdown} testid="snapshot-proformas" />
            <SnapshotCard title="Dealer Agreements" icon={Handshake}  data={dealersBreakdown}   testid="snapshot-dealers" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            {commissions.loading || commissions.data?.unavailable ? (
              <>
                <CommissionUnavailable title="Contracts Remitted" icon={CheckCircle2} testid="snapshot-remitted"
                  body={`${groupLabel} · MTD / YTD`}
                  reason={commissions.data?.reason} />
                <CommissionUnavailable title="Contracts Sold" icon={DollarSign} testid="snapshot-sold"
                  body={`${groupLabel} · MTD / YTD`}
                  reason={commissions.data?.reason} />
                <CommissionUnavailable title="Trending Remitted" icon={TrendingUp} testid="snapshot-trending"
                  body="Projected EOM based on current pace"
                  reason={commissions.data?.reason} />
              </>
            ) : (
              <>
                <CommissionCard title="Contracts Remitted" icon={CheckCircle2} testid="snapshot-remitted"
                  data={commissions.data?.remitted} groupLabel={groupLabel} mode="counts" />
                <CommissionCard title="Contracts Sold" icon={DollarSign} testid="snapshot-sold"
                  data={commissions.data?.sold} groupLabel={groupLabel} mode="counts" />
                <CommissionCard title="Trending Remitted" icon={TrendingUp} testid="snapshot-trending"
                  data={commissions.data?.trending} groupLabel={groupLabel} mode="trending" />
              </>
            )}
          </div>
        </Card>

        {/* ============ SECTION 1 · PROPOSALS ============ */}
        <SectionHeader
          kicker="Section 01"
          title="Proposals"
          subtitle="Every dealership pitch generated in this window — plus link engagement."
          cta={<Link to="/proposals/new" data-testid="new-proposal-cta">
            <Button className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold">
              <FilePlus size={14} className="mr-2" /> New proposal
            </Button>
          </Link>}
        />

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="proposals-kpis">
          <StatCard label="Proposals" value={fmt(analytics?.totals?.proposals)} icon={FileText} testid="kpi-proposals" />
          <StatCard label="Vehicles quoted" value={fmt(analytics?.totals?.vehicles)} icon={BarChart3} sub={analytics?.totals?.avg_vehicles ? `${analytics.totals.avg_vehicles}/proposal` : null} testid="kpi-vehicles" />
          <StatCard label="Emails sent" value={fmt(analytics?.totals?.emails_sent)} icon={Mail} testid="kpi-emails" />
          <StatCard label="Link clicks" value={fmt(analytics?.totals?.clicks)} icon={MousePointerClick} sub={analytics ? `${analytics.totals.clicks_email} email · ${analytics.totals.clicks_share} direct` : null} testid="kpi-clicks" />
          <StatCard label="Click-through" value={pct(analytics?.totals?.ctr_percent)} icon={TrendingUp} sub="Clicks ÷ proposals" testid="kpi-ctr" />
        </div>

        {/* Weekly chart */}
        <Card className="p-6 mb-6 border-[#E9ECEF] rounded-sm" data-testid="analytics-chart">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-4">Proposals per week</div>
          {analytics?.time_series?.length ? (
            <div className="flex items-end gap-2 h-40">
              {analytics.time_series.map((row) => (
                <div key={row.week} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="text-[10px] font-mono font-bold text-[#0B132B] opacity-0 group-hover:opacity-100 transition-opacity">{row.count}</div>
                  <div className="w-full bg-[#0B132B] hover:bg-[#D4AF37] transition-colors rounded-t-sm"
                    style={{ height: `${(row.count / chartMax) * 100}%`, minHeight: "4px" }}
                    title={`Week of ${row.week}: ${row.count}`} />
                  <div className="text-[8px] font-mono text-[#6C757D] rotate-45 origin-top-left translate-y-2 whitespace-nowrap">{row.week.slice(5)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-[#6C757D] italic py-10 text-center">No proposals in this date range.</div>
          )}
        </Card>

        {/* Agency + agent rollups */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {canDrillAgency && (
            <Card className="p-6 border-[#E9ECEF] rounded-sm" data-testid="analytics-agencies-table">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-4 flex items-center gap-2"><Building2 size={14} /> By agency</div>
              <RollupTable rows={analytics?.agencies || []} keyCol="agency" onClick={(row) => setAgencyFilter(row.agency)} />
            </Card>
          )}
          <Card className={`p-6 border-[#E9ECEF] rounded-sm ${!canDrillAgency ? "lg:col-span-2" : ""}`} data-testid="analytics-agents-table">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-4 flex items-center gap-2"><Users size={14} /> Top agents</div>
            <RollupTable rows={(analytics?.agents || []).slice(0, 10)} keyCol="agent_name" agentTable onClick={(row) => setAgentFilter(row.agent_id)} />
          </Card>
        </div>

        {/* Proposals grid moved to /proposals under Marketing so the dashboard
            stays snapshot-only. See `pages/Proposals.jsx`. */}

        {/* ============ SECTION 2 · PROFORMAS ============ */}
        <SectionHeader
          kicker="Section 02"
          title="Proformas"
          subtitle="Reinsurance projections built in this window."
          cta={<Link to="/proforma" data-testid="proforma-link">
            <Button variant="outline" className="h-10 rounded-sm border-[#0B132B]/20">
              Open proformas <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>}
        />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="proformas-kpis">
          <StatCard label="Proformas built" value={fmt(analytics?.proformas?.count)} icon={FileText} testid="kpi-proformas" />
          <StatCard label="Sent to dealers" value={fmt(analytics?.proformas?.sent)} icon={Send} sub={`${fmt(analytics?.proformas?.clicked || 0)} opened by a dealer`} testid="kpi-proforma-sent" />
          <StatCard
            label="Converted"
            value={fmt(
              (analytics?.proformas?.converted_any) ??
              ((analytics?.proformas?.converted_to_proposal || 0) + (analytics?.proformas?.converted_to_dealer || 0) - (analytics?.proformas?.converted_to_both || 0))
            )}
            icon={TrendingUp}
            sub={`${fmt(analytics?.proformas?.converted_to_proposal || 0)} → proposal · ${fmt(analytics?.proformas?.converted_to_dealer || 0)} → dealer · ${fmt(analytics?.proformas?.converted_to_both || 0)} → both`}
            testid="kpi-proforma-converted"
          />
        </div>
        <ProformasList proformas={proformas} />

        {/* ============ SECTION 3 · DEALER SIGNUPS ============ */}
        <SectionHeader
          kicker="Section 03"
          title="Dealer signups"
          subtitle="How many packets went out, how many came back signed, and the closing ratio against your proposals."
          cta={<Link to="/dealers" data-testid="dealers-link">
            <Button variant="outline" className="h-10 rounded-sm border-[#0B132B]/20">
              Manage dealers <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="dealers-kpis">
          <StatCard label="Sent for e-sign" value={fmt(analytics?.dealers?.sent)} icon={Mail} testid="kpi-dealers-sent" />
          <StatCard label="Dealer e-signed" value={fmt((analytics?.dealers?.dealer_signed || 0) + (analytics?.dealers?.executed || 0))} icon={PenLine} testid="kpi-dealers-signed" />
          <StatCard label="Fully executed" value={fmt(analytics?.dealers?.executed)} icon={CheckCircle2} testid="kpi-dealers-executed" />
          <StatCard label="Sign rate" value={pct(analytics?.dealers?.sign_rate_percent)} icon={TrendingUp} sub="Signed ÷ sent" testid="kpi-dealers-sign-rate" />
        </div>

        {/* Closing ratio callout */}
        <Card className="p-6 mb-6 border-[#0B132B] border-2 rounded-sm bg-gradient-to-br from-[#0B132B] to-[#1C2541] text-white" data-testid="closing-ratio-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Closing ratio</div>
              <div className="font-display text-4xl sm:text-5xl font-black mt-1">{pct(analytics?.closing?.closing_ratio_percent)}</div>
              <div className="text-sm text-white/70 mt-2">
                {fmt(analytics?.closing?.closed_proposals)} of {fmt(analytics?.totals?.proposals)} proposals resulted in an executed agreement.
              </div>
            </div>
            <div className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-white/60 max-w-xs text-right">
              Matched by dealership name — link exact dealers on a proposal to override.
            </div>
          </div>
        </Card>

        {/* Dealers list */}
        <DealersList dealers={dealers} />
      {pdfDialog}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Snapshot card — a single metric with bar↔pie toggle. Total is emphasised so
// the eye lands on it first; the chart underneath shows the breakdown per
// agent (or agency for super-admin). Empty state stays neutral.
// -----------------------------------------------------------------------------
function SnapshotCard({ title, icon: Icon, data, testid }) {
  const [mode, setMode] = useState("bar");
  const total = (data || []).reduce((s, r) => s + (r.value || 0), 0);
  const top = (data || []).slice(0, 5);
  return (
    <Card className="p-4 border-[#E9ECEF] rounded-sm bg-white" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-[#D4AF37]" />}
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">{title}</div>
        </div>
        <div className="flex rounded-sm overflow-hidden border border-[#E9ECEF]" role="tablist">
          <button
            type="button"
            aria-pressed={mode === "bar"}
            onClick={() => setMode("bar")}
            className={`h-7 w-7 flex items-center justify-center ${mode === "bar" ? "bg-[#0B132B] text-white" : "bg-white text-[#6C757D] hover:text-[#0B132B]"}`}
            data-testid={`${testid}-bar`}
            title="Bar chart"
          >
            <BarChart2 size={12} />
          </button>
          <button
            type="button"
            aria-pressed={mode === "pie"}
            onClick={() => setMode("pie")}
            className={`h-7 w-7 flex items-center justify-center border-l border-[#E9ECEF] ${mode === "pie" ? "bg-[#0B132B] text-white" : "bg-white text-[#6C757D] hover:text-[#0B132B]"}`}
            data-testid={`${testid}-pie`}
            title="Pie chart"
          >
            <PieIcon size={12} />
          </button>
        </div>
      </div>
      <div className="font-display text-3xl font-black text-[#0B132B] font-mono leading-none" data-testid={`${testid}-total`}>{fmt(total)}</div>
      <div className="text-[10px] text-[#6C757D] mt-1">
        {data.length} {data.length === 1 ? "contributor" : "contributors"}
      </div>
      <div className="h-40 mt-3">
        {total === 0 ? (
          <div className="h-full grid place-items-center text-[11px] text-[#6C757D] italic">No activity in this range.</div>
        ) : mode === "bar" ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10, fill: "#0B132B" }} />
              <Tooltip cursor={{ fill: "#0B132B10" }} contentStyle={{ fontSize: 11, borderRadius: 2, border: "1px solid #E9ECEF" }} />
              <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                {top.map((entry, i) => <Cell key={entry.name || `bar-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={top} dataKey="value" nameKey="name" innerRadius={30} outerRadius={60} paddingAngle={2}>
                {top.map((entry, i) => <Cell key={entry.name || `slice-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2, border: "1px solid #E9ECEF" }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Commission stub — placeholder for MTD/YTD contract counts + trending. Real
// data source will land when the dealercomm sibling exposes a REST endpoint.
// -----------------------------------------------------------------------------
function CommissionStub({ title, icon: Icon, body, testid }) {
  return (
    <Card className="p-4 border-dashed border-2 border-[#E9ECEF] rounded-sm bg-[#F8F9FA]" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-[#0B132B]/40" />}
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]/70">{title}</div>
        </div>
        <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-sm px-1.5 py-0.5">
          <Sparkles size={9} className="inline mr-0.5" /> Coming soon
        </span>
      </div>
      <div className="font-display text-2xl font-black text-[#0B132B]/30 font-mono leading-none">—</div>
      <div className="text-[10px] text-[#6C757D] mt-2 leading-tight">{body}</div>
      <div className="text-[10px] text-[#6C757D] mt-2 leading-tight italic">
        Awaiting the Commissions API integration.
      </div>
    </Card>
  );
}

// ==================== components ====================

function SectionHeader({ kicker, title, subtitle, cta }) {
  return (
    <div className="mt-10 mb-4 pt-6 border-t-2 border-[#0B132B] flex items-end justify-between flex-wrap gap-3" data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold">{kicker}</div>
        <h2 className="font-display text-2xl sm:text-3xl font-black text-[#0B132B] tracking-tight leading-none mt-1">{title}</h2>
        <p className="text-xs sm:text-sm text-[#6C757D] mt-1 max-w-2xl">{subtitle}</p>
      </div>
      {cta}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, sub, testid }) {
  return (
    <Card className="p-4 sm:p-5 border-[#E9ECEF] rounded-sm" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[9px] uppercase tracking-[0.24em] font-bold text-[#6C757D]">{label}</div>
        {Icon && <Icon size={14} className="text-[#D4AF37]" />}
      </div>
      <div className="font-display text-2xl sm:text-3xl font-black text-[#0B132B] font-mono leading-none">{value}</div>
      {sub && <div className="text-[10px] text-[#6C757D] mt-2 leading-tight">{sub}</div>}
    </Card>
  );
}

function RollupTable({ rows, keyCol, agentTable, onClick }) {
  if (!rows.length) return <div className="text-sm text-[#6C757D] italic py-6 text-center">No data yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[9px] uppercase tracking-[0.14em] text-[#6C757D]">
          <tr className="border-b border-[#E9ECEF]">
            <th className="text-left py-2 font-bold">{agentTable ? "Agent" : "Agency"}</th>
            <th className="text-right py-2 font-bold">Proposals</th>
            <th className="text-right py-2 font-bold">Vehicles</th>
            <th className="text-right py-2 font-bold">Clicks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r[keyCol] + i} onClick={() => onClick && onClick(r)}
              className="border-b border-[#E9ECEF] hover:bg-[#F8F9FA] cursor-pointer"
              data-testid={agentTable ? `analytics-agent-row-${r.agent_id}` : `analytics-agency-row-${r.agency}`}>
              <td className="py-2 font-semibold text-[#0B132B]">
                {agentTable ? (
                  <div>
                    <div>{r.agent_name}</div>
                    <div className="text-[10px] text-[#6C757D] uppercase tracking-[0.12em]">{r.agency}</div>
                  </div>
                ) : r.agency}
              </td>
              <td className="py-2 text-right font-mono font-bold text-[#0B132B]">{fmt(r.proposals)}</td>
              <td className="py-2 text-right font-mono text-[#0B132B]">{fmt(r.vehicles)}</td>
              <td className="py-2 text-right font-mono text-[#D4AF37] font-bold">{fmt(r.clicks)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProposalsList({ proposals, loading, dealerLookup, onDelete, onDownload }) {
  if (loading) return <div className="text-sm text-[#6C757D] py-6">Loading proposals…</div>;
  if (proposals.length === 0) {
    return (
      <Card className="border-dashed border-2 border-[#E9ECEF] bg-white p-10 text-center rounded-sm" data-testid="empty-proposals">
        <FilePlus size={26} className="mx-auto text-[#6C757D]" />
        <h3 className="font-display text-xl font-bold text-[#0B132B] mt-2">No proposals yet</h3>
        <Link to="/proposals/new"><Button className="mt-4 h-10 rounded-sm bg-[#0B132B] text-white">New proposal</Button></Link>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="proposals-grid">
      {proposals.slice(0, 9).map((p) => (
        <ProposalCard key={p.id} p={p} linkedDealer={dealerLookup(p.dealership)} onDelete={() => onDelete(p.id)} onDownload={() => onDownload(p.id, p.title)} />
      ))}
      {proposals.length > 9 && (
        <Link to="/proposals" className="hidden md:flex items-center justify-center text-sm font-semibold text-[#0B132B] hover:text-[#D4AF37] border-2 border-dashed border-[#E9ECEF] rounded-sm min-h-[240px] transition-colors" data-testid="see-all-proposals">
          See all {proposals.length} <ArrowRight size={14} className="ml-2" />
        </Link>
      )}
    </div>
  );
}

function ProposalCard({ p, linkedDealer, onDelete, onDownload }) {
  const firstImg = p.vehicles?.[0]?.vehicle?.image_url;
  const dealerStatus = linkedDealer?.status;
  return (
    <Card className="group bg-white border border-[#E9ECEF] rounded-sm overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`proposal-card-${p.id}`}>
      <div className="relative h-32 bg-[#0B132B] overflow-hidden">
        {firstImg ? (
          <img src={firstImg} alt="" className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0B132B] to-[#1C2541] flex items-center justify-center">
            <Car className="text-[#D4AF37]" size={36} />
          </div>
        )}
        <div className="absolute top-2 left-2 px-2 py-1 rounded-sm bg-[#D4AF37] text-[#0B132B] text-[10px] uppercase tracking-[0.2em] font-bold">
          {p.vehicles?.length || 0} vehicles
        </div>
        <div className="absolute top-2 right-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="secondary" className="h-8 w-8 rounded-sm bg-white/95 hover:bg-white" data-testid={`proposal-menu-${p.id}`}>
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={onDownload} data-testid={`proposal-download-${p.id}`}>
                <Download size={14} className="mr-2" /> Download PDF
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/proposals/${p.id}/view`} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={14} className="mr-2" /> Open view
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="text-red-600" data-testid={`proposal-delete-${p.id}`}>
                <Trash2 size={14} className="mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="p-4">
        <Link to={`/proposals/${p.id}`} className="block group-hover:text-[#D4AF37] transition-colors">
          <h3 className="font-display text-base font-bold text-[#0B132B] leading-snug line-clamp-2" data-testid={`proposal-title-${p.id}`}>{p.title}</h3>
        </Link>
        {p.dealership && (
          <div className="mt-2 text-xs text-[#6C757D] flex items-center gap-1.5"><Building2 size={12} /> {p.dealership}</div>
        )}
        {linkedDealer ? (
          <div className={`mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-1 rounded-sm border ${
            dealerStatus === "executed" ? "bg-green-50 border-green-200 text-green-700" :
            dealerStatus === "dealer_signed" ? "bg-amber-50 border-amber-200 text-amber-800" :
            dealerStatus === "sent" ? "bg-blue-50 border-blue-200 text-blue-700" :
            "bg-[#F8F9FA] border-[#E9ECEF] text-[#6C757D]"
          }`} data-testid={`proposal-dealer-link-${p.id}`}>
            <Handshake size={11}/> {dealerStatus === "executed" ? "Signed & executed" : dealerStatus === "dealer_signed" ? "Dealer signed" : dealerStatus === "sent" ? "Sent to dealer" : "Draft"}
          </div>
        ) : p.dealership ? (
          <div className="mt-3 text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">No linked signup</div>
        ) : null}
      </div>
    </Card>
  );
}

function ProformasList({ proformas }) {
  if (proformas.length === 0) {
    return (
      <Card className="border-dashed border-2 border-[#E9ECEF] bg-white p-10 text-center rounded-sm" data-testid="empty-proformas">
        <FileText size={26} className="mx-auto text-[#6C757D]" />
        <h3 className="font-display text-xl font-bold text-[#0B132B] mt-2">No proformas yet</h3>
        <Link to="/proforma"><Button className="mt-4 h-10 rounded-sm bg-[#0B132B] text-white">Open proformas</Button></Link>
      </Card>
    );
  }
  return (
    <Card className="border-[#E9ECEF] rounded-sm overflow-hidden" data-testid="proformas-list">
      {proformas.slice(0, 6).map((p, i) => {
        return (
          <Link key={p.id} to={`/proforma/${p.id}`}
            className={`flex items-center gap-4 px-5 py-4 hover:bg-[#F8F9FA] transition-colors ${i > 0 ? "border-t border-[#E9ECEF]" : ""}`}
            data-testid={`proforma-row-${p.id}`}>
            <FileText size={16} className="text-[#0B132B] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[#0B132B] truncate">{p.title}</div>
              <div className="text-[11px] text-[#6C757D] mt-0.5">Created {new Date(p.created_at).toLocaleDateString()}</div>
            </div>
            <ArrowRight size={14} className="text-[#6C757D]"/>
          </Link>
        );
      })}
    </Card>
  );
}

function DealersList({ dealers }) {
  if (dealers.length === 0) {
    return (
      <Card className="border-dashed border-2 border-[#E9ECEF] bg-white p-10 text-center rounded-sm" data-testid="empty-dealers">
        <Handshake size={26} className="mx-auto text-[#6C757D]" />
        <h3 className="font-display text-xl font-bold text-[#0B132B] mt-2">No dealers yet</h3>
        <Link to="/dealers"><Button className="mt-4 h-10 rounded-sm bg-[#0B132B] text-white">Add dealers</Button></Link>
      </Card>
    );
  }
  const STATUS = {
    draft:         { label: "Draft",           tone: "bg-[#F8F9FA] text-[#6C757D] border-[#E9ECEF]" },
    sent:          { label: "Sent",            tone: "bg-blue-50 text-blue-700 border-blue-200" },
    dealer_signed: { label: "Awaiting counter", tone: "bg-amber-50 text-amber-800 border-amber-200" },
    executed:      { label: "Executed",        tone: "bg-green-50 text-green-700 border-green-200" },
  };
  return (
    <Card className="border-[#E9ECEF] rounded-sm overflow-hidden" data-testid="dealers-list">
      {dealers.slice(0, 8).map((d, i) => {
        const st = STATUS[d.status] || STATUS.draft;
        return (
          <Link key={d.id} to={`/dealers/${d.id}`}
            className={`flex items-center gap-4 px-5 py-4 hover:bg-[#F8F9FA] transition-colors ${i > 0 ? "border-t border-[#E9ECEF]" : ""}`}
            data-testid={`dashboard-dealer-row-${d.id}`}>
            <Handshake size={16} className="text-[#0B132B] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[#0B132B] truncate">{d.dealer_name}</div>
              <div className="text-[11px] text-[#6C757D] mt-0.5 truncate">{d.contact_email}</div>
            </div>
            <span className={`text-[9px] uppercase tracking-[0.14em] font-bold px-2 py-1 border rounded-sm shrink-0 ${st.tone}`}>{st.label}</span>
            <ArrowRight size={14} className="text-[#6C757D] shrink-0"/>
          </Link>
        );
      })}
    </Card>
  );
}
